/**
 * Entity Link Utilities
 *
 * Detects entity identifiers (ORD-xxx, SHP-xxx, etc.) in text
 * and resolves them to navigation routes.
 *
 * DRY: Patterns + tableName mappings live in `@axon/common`'s
 * `CHATBOT_ENTITY_LINK_SPECS` so backend extraction
 * (`RECORD_IDENTIFIER_PATTERNS`) and frontend detection share one
 * source of truth — closes the drift class flagged in #370.
 *
 * Phase 19B: Interactive Entity Links
 *
 * @module ChatbotLinkUtils
 */

import { CHATBOT_ENTITY_LINK_SPECS } from '@axon/common';

// ============================================================================
// Types
// ============================================================================

/**
 * Detected entity link match in text
 */
export interface EntityLinkMatch {
  /** The matched identifier string (e.g., "ORD-000056") */
  id: string;
  /** Entity table name (e.g., "order_core") */
  tableName: string;
  /** Start index in the source text */
  startIndex: number;
  /** End index in the source text */
  endIndex: number;
}

/**
 * Entity link pattern definition (per-instance regex with `gi` flags
 * for stateful `exec()` iteration in `detectEntityLinks`).
 */
interface EntityLinkPattern {
  /** Regex to match the identifier */
  pattern: RegExp;
  /** Entity table name */
  tableName: string;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Entity link patterns for frontend detection. Reconstructed from
 * `CHATBOT_ENTITY_LINK_SPECS` with the `gi` flag added so
 * `regex.exec(text)` can iterate matches via `lastIndex`. Backend
 * stays on `i`-only because its consumers call stateless `.test()`.
 */
const ENTITY_LINK_PATTERNS: ReadonlyArray<EntityLinkPattern> = CHATBOT_ENTITY_LINK_SPECS.map(
  (spec) => ({
    pattern: new RegExp(spec.source, 'gi'),
    tableName: spec.tableName,
  })
);

/**
 * Frontend-safe navigation route mapping.
 * Maps entity table names to their detail page route template.
 *
 * DRY: Mirrors backend NAVIGATION_ROUTES from chatbot.constants.ts.
 * Only includes entities with detail pages (not dashboard, finance, etc.).
 */
const NAVIGATION_ROUTES_CLIENT: Record<string, string> = {
  order_core: '/orders/:id',
  shipment_core: '/shipments/:id',
  customers: '/customers/:id',
  drivers: '/fleet/drivers/:id',
  power_units: '/fleet/power-units/:id',
  trips: '/trips/:id',
  route_core: '/routes/:id',
  zone_definitions: '/zones/:id',
  invoices: '/finance/invoices/:id',
};

// ============================================================================
// Functions
// ============================================================================

/**
 * Detect all entity identifiers in a text string.
 *
 * Scans text for patterns like ORD-000056, SHP-123, INV-100, etc.
 * Returns matches sorted by position in text.
 *
 * @param text - Text to scan for entity identifiers
 * @returns Array of entity link matches, sorted by startIndex
 */
export function detectEntityLinks(text: string): EntityLinkMatch[] {
  if (!text) return [];

  const matches: EntityLinkMatch[] = [];
  const seen = new Set<string>();

  for (const { pattern, tableName } of ENTITY_LINK_PATTERNS) {
    // Reset lastIndex on the shared regex (global flag) instead of recreating
    pattern.lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = pattern.exec(text)) !== null) {
      const id = match[0].toUpperCase();
      // Deduplicate: same ID at same position
      const key = `${id}:${match.index}`;
      if (!seen.has(key)) {
        seen.add(key);
        matches.push({
          id,
          tableName,
          startIndex: match.index,
          endIndex: match.index + match[0].length,
        });
      }
    }
  }

  // Sort by position in text
  matches.sort((a, b) => a.startIndex - b.startIndex);
  return matches;
}

/**
 * Resolve an entity table name and ID to a navigation path.
 *
 * @param tableName - Entity table name (e.g., "order_core")
 * @param entityId - Entity identifier (e.g., "ORD-000056")
 * @returns Resolved path (e.g., "/orders/ORD-000056") or null if no route
 */
export function getEntityRoute(tableName: string, entityId: string): string | null {
  const template = NAVIGATION_ROUTES_CLIENT[tableName];
  if (!template) return null;
  return template.replace(':id', entityId);
}

/**
 * Pre-compiled per-spec regex anchored to the FULL string, used by
 * `isEntityId` for cell-rendering hot paths. Anchors prevent matching
 * IDs embedded in longer strings (which is what `detectEntityLinks`
 * is for).
 */
const FULL_STRING_ENTITY_PATTERNS: ReadonlyArray<RegExp> = CHATBOT_ENTITY_LINK_SPECS.map(
  (spec) => new RegExp(`^${spec.source}$`, 'i')
);

/**
 * Check if a cell value contains an entity identifier.
 * Quick check without full detection — for performance in table rendering.
 *
 * @param value - Cell value to check
 * @returns true if the value looks like an entity ID
 */
export function isEntityId(value: string): boolean {
  if (!value || value.length < 4) return false;
  const trimmed = value.trim();
  return FULL_STRING_ENTITY_PATTERNS.some((rx) => rx.test(trimmed));
}

/**
 * Get the entity table name for a given entity ID.
 *
 * @param entityId - Entity identifier (e.g., "ORD-000056")
 * @returns Entity table name or null
 */
export function getTableNameForEntityId(entityId: string): string | null {
  const upper = entityId.toUpperCase().trim();
  for (const { pattern, tableName } of ENTITY_LINK_PATTERNS) {
    const regex = new RegExp(pattern.source, 'i');
    if (regex.test(upper)) {
      return tableName;
    }
  }
  return null;
}
